import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import proxy from '../../api/axios';
import { toast } from 'react-toastify';
import {
  ACCENT,
  AdminFormSection,
  adminFieldSx,
  roleColor,
  useAdminPageTheme,
} from './adminUi';

const FORM_ID = 'admin-user-form';

const ROLES = [
  { value: 'user', label: 'User', hint: 'Standard platform access' },
  { value: 'softadmin', label: 'Soft Admin', hint: 'Limited admin tools' },
  { value: 'admin', label: 'Admin', hint: 'Full admin access' },
];

const CreateUser = ({ user, onClose, onSavingChange }) => {
  const { border, muted, isDark } = useAdminPageTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [hasNeetAccess, setHasNeetAccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setRole(user.role || 'user');
      setIsPremiumUser(Boolean(user.isPremiumUser));
      setHasNeetAccess(Boolean(user.hasNeetAccess));
      setPassword('');
    } else {
      setName('');
      setEmail('');
      setPassword('');
      setRole('user');
      setIsPremiumUser(false);
      setHasNeetAccess(false);
    }
  }, [user]);

  useEffect(() => {
    onSavingChange?.(submitting);
  }, [submitting, onSavingChange]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const userData = { name: name.trim(), email: email.trim(), role, isPremiumUser, hasNeetAccess };
    if (password.trim()) {
      userData.password = password;
    } else if (!user) {
      toast.error('Password is required for new users');
      setSubmitting(false);
      return;
    }

    try {
      if (user) {
        const { data } = await proxy.put(`/users/${user._id}`, userData);
        toast.success(data.message || 'User updated');
      } else {
        const { data } = await proxy.post('/users', userData);
        toast.success(data.message || 'User created');
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not save user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" id={FORM_ID} onSubmit={handleSubmit}>
      <AdminFormSection
        title="Profile"
        description="Basic account information visible across the platform."
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <TextField
            label="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            size="small"
            sx={adminFieldSx}
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            size="small"
            sx={adminFieldSx}
          />
        </Box>
        <TextField
          label={user ? 'New password' : 'Password'}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required={!user}
          size="small"
          fullWidth
          sx={{ ...adminFieldSx, mt: 1.5 }}
          helperText={
            user
              ? 'Leave blank to keep the current password'
              : 'Minimum 6 characters recommended'
          }
        />
      </AdminFormSection>

      <AdminFormSection
        title="Access & permissions"
        description="Control what this account can do on CrafterHack."
      >
        <Typography variant="caption" sx={{ color: muted, fontWeight: 700, display: 'block', mb: 0.75 }}>
          Role
        </Typography>
        <ToggleButtonGroup
          exclusive
          value={role}
          onChange={(_, v) => v && setRole(v)}
          size="small"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            mb: 1.5,
            '& .MuiToggleButtonGroup-grouped': {
              border: `1px solid ${border} !important`,
              borderRadius: '8px !important',
              mx: 0,
            },
            '& .Mui-selected': {
              bgcolor: `${alpha(ACCENT, isDark ? 0.22 : 0.1)} !important`,
              color: `${ACCENT} !important`,
              fontWeight: 800,
            },
          }}
        >
          {ROLES.map((r) => (
            <ToggleButton key={r.value} value={r.value} sx={{ textTransform: 'none', px: 1.5 }}>
              <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 16, mr: 0.5, color: roleColor(r.value) }} />
              {r.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: muted, display: 'block', mb: 1.5 }}>
          {ROLES.find((r) => r.value === role)?.hint}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.25,
            borderRadius: 1.5,
            border: `1px solid ${border}`,
            bgcolor: isDark ? '#161b22' : '#fff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WorkspacePremiumOutlinedIcon sx={{ color: '#d97706', fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Premium user</Typography>
              <Typography variant="caption" sx={{ color: muted }}>
                Grants premium features and content access
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={isPremiumUser}
            onChange={(e) => setIsPremiumUser(e.target.checked)}
            color="primary"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.25,
            borderRadius: 1.5,
            border: `1px solid ${border}`,
            bgcolor: isDark ? '#161b22' : '#fff',
            mt: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FactCheckOutlinedIcon sx={{ color: '#0ea5e9', fontSize: 20 }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>NEET test access</Typography>
              <Typography variant="caption" sx={{ color: muted }}>
                Grants access to timed NEET mock tests
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={hasNeetAccess}
            onChange={(e) => setHasNeetAccess(e.target.checked)}
            color="primary"
          />
        </Box>
      </AdminFormSection>
    </Box>
  );
};

CreateUser.formId = FORM_ID;
export default CreateUser;
